import { type CompanyLookupOutput, formatCompanyAddress } from '@fos/shared/contracts/company-registry';
import type { ClientCompanyInput } from '@fos/shared/contracts/crm';
import type { MailThreadDetail } from '@fos/shared/contracts/mail';
import { normalizeNip, taxIdSchema } from '@fos/shared/nip';
import { useEffect, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';
import { ClientPicker } from '../../clients/components/ClientPicker';
import { NipLookupField, registrySourceLabel } from '../../clients/components/CompanyLookup';
import { useMailRefresh } from '../hooks';

/**
 * „Przypisz” (Figma 02): wybór klienta wątku. Przy niejednoznacznym nadawcy najpierw kandydaci (kontakt u kilku
 * klientów), niżej dowolny klient z zakresu osoby. Przypisanie przenosi wszystkie wiadomości wątku („Ręcznie”).
 */
export function AssignClientDialog({
  open,
  onOpenChange,
  thread,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  thread: Pick<MailThreadDetail, 'id' | 'subject' | 'candidates' | 'unresolved' | 'client'>;
}) {
  const refresh = useMailRefresh();
  const [clientId, setClientId] = useState<string | null>(null);
  const assign = trpc.mail.assignThread.useMutation({
    onSuccess: async (detail) => {
      await refresh();
      toast.success(`Wątek przypisany: ${detail.client?.name ?? 'klient'}`);
      onOpenChange(false);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy otwarciu
  useEffect(() => {
    if (open) {
      setClientId(thread.candidates[0]?.clientId ?? thread.client?.id ?? null);
      assign.reset();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Przypisz do klienta"
      description={`Wątek „${thread.subject}” i wszystkie jego wiadomości trafią do historii wybranego klienta.`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            disabled={!clientId}
            loading={assign.isPending}
            onClick={() => clientId && assign.mutate({ id: thread.id, clientId })}
          >
            Przypisz
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {assign.isError && <FormMessage>{errorMessage(assign.error, 'Nie udało się przypisać wątku.')}</FormMessage>}
        {thread.candidates.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-caption font-medium text-fg-secondary">Nadawca jest kontaktem klientów</legend>
            {thread.candidates.map((c) => (
              <label
                key={c.clientId}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-body-s',
                  clientId === c.clientId ? 'border-accent bg-accent-subtle' : 'border-line hover:bg-surface',
                )}
              >
                <input
                  type="radio"
                  name="mail-candidate"
                  className="accent-accent"
                  checked={clientId === c.clientId}
                  onChange={() => setClientId(c.clientId)}
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-fg">{c.clientName}</span>
                  {c.contactName && <span className="text-fg-muted"> · {c.contactName}</span>}
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <Field label={thread.candidates.length > 0 ? 'Albo inny klient' : 'Klient'}>
          <ClientPicker value={clientId} onChange={setClientId} excludePartial />
        </Field>
      </div>
    </Dialog>
  );
}

/** Nazwa klienta z adresu nadawcy: imię i nazwisko albo domena („nowafirma.pl”); darmowe skrzynki — nazwa przed @. */
export function suggestClientName(counterpart: { email: string; name?: string } | null): string {
  if (!counterpart) return '';
  const [local = '', domain = ''] = counterpart.email.split('@');
  if (/^(gmail|googlemail|wp|o2|onet|interia|outlook|hotmail|yahoo|icloud|proton(mail)?)\./i.test(domain)) {
    return counterpart.name?.trim() || local;
  }
  return domain || counterpart.name?.trim() || local;
}

/** Dane firmy do zapisu: z rejestru („Użyj danych”) albo sam wpisany NIP. */
export function senderCompany(nip: string, used: CompanyLookupOutput | null): ClientCompanyInput | undefined {
  const c = used?.company;
  if (used && c && used.nip === normalizeNip(nip)) {
    return {
      nip: c.nip,
      legalName: c.legalName,
      ...(c.regon ? { regon: c.regon } : {}),
      ...(c.krs ? { krs: c.krs } : {}),
      vatStatus: c.vatStatus,
      address: c.address,
      registrySource: used.source,
      registryCheckedAt: new Date(used.fetchedAt).toISOString(),
    };
  }
  return nip.trim() ? { nip: nip.trim() } : undefined;
}

/**
 * „Nowy klient” (Figma 02, tylko ręcznie — system nie tworzy klientów z maili): klient + główny kontakt z adresu
 * nadawcy, wątek przypisany. Wymaga Klienci: Edycja. Fala 7: pole NIP z danymi firmy z rejestru (jak „Nowy klient”).
 */
export function CreateClientFromSenderDialog({
  open,
  onOpenChange,
  thread,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  thread: Pick<MailThreadDetail, 'id' | 'counterpart'>;
}) {
  const refresh = useMailRefresh();
  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [nip, setNip] = useState('');
  const [used, setUsed] = useState<CompanyLookupOutput | null>(null);
  const [nipError, setNipError] = useState<string | null>(null);
  const create = trpc.mail.createClientFromSender.useMutation({
    onSuccess: async (res) => {
      await refresh();
      toast.success(`Dodano klienta: ${res.thread.client?.name ?? name}`);
      onOpenChange(false);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy otwarciu
  useEffect(() => {
    if (open) {
      setName(suggestClientName(thread.counterpart));
      setContactName(thread.counterpart?.name ?? '');
      setNip('');
      setUsed(null);
      setNipError(null);
      create.reset();
    }
  }, [open]);

  const nameError = create.isError && !name.trim() ? 'Podaj nazwę klienta' : null;

  function submit() {
    if (!name.trim()) return;
    if (nip.trim() && !taxIdSchema.safeParse(nip).success) {
      setNipError(taxIdSchema.safeParse(nip).error?.issues[0]?.message ?? 'Nieprawidłowy NIP');
      return;
    }
    const company = senderCompany(nip, used);
    create.mutate({
      id: thread.id,
      name: name.trim(),
      ...(contactName.trim() ? { contactName: contactName.trim() } : {}),
      ...(company ? { company } : {}),
    });
  }

  function applyRegistryResult(result: CompanyLookupOutput) {
    setUsed(result);
    if (result.suggestedName) setName(result.suggestedName);
  }

  const usedCompany = used?.company && used.nip === normalizeNip(nip) ? used : null;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Nowy klient z nadawcy"
      description={thread.counterpart ? `Kontakt: ${thread.counterpart.email}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" disabled={!name.trim()} loading={create.isPending} onClick={submit}>
            Dodaj i przypisz
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {create.isError && !nameError && (
          <FormMessage>{errorMessage(create.error, 'Nie udało się dodać klienta.')}</FormMessage>
        )}
        <NipLookupField
          value={nip}
          onChange={(v) => {
            setNip(v);
            setNipError(null);
          }}
          onUse={applyRegistryResult}
          usedLabel={usedCompany ? registrySourceLabel(usedCompany.source, usedCompany.fetchedAt) : null}
          error={nipError}
        />
        {usedCompany?.company && (
          <div className="flex flex-wrap items-start justify-between gap-2 rounded-md bg-surface px-3 py-2 text-body-s">
            <p className="min-w-0">
              <span className="block font-medium text-fg">{usedCompany.company.legalName}</span>
              <span className="text-fg-secondary">{formatCompanyAddress(usedCompany.company.address)}</span>
            </p>
            <Button variant="ghost" size="s" onClick={() => setUsed(null)}>
              Nie używaj
            </Button>
          </div>
        )}
        <Field label="Nazwa klienta" error={nameError}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
        </Field>
        <Field label="Osoba kontaktowa" hint="Imię i nazwisko nadawcy; adres email z wiadomości.">
          <Input value={contactName} onChange={(e) => setContactName(e.target.value)} maxLength={200} />
        </Field>
      </form>
    </Dialog>
  );
}
